import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, ShieldCheck, CreditCard, MapPinned, Mail, Phone, ArrowRight, Sparkles } from 'lucide-react';
import { Reveal, useTilt } from './site/motion';

const VALUES = [
  {
    icon: Clock,
    titleKey: 'about.value1Title',
    titleFallback: 'Instant booking',
    descKey: 'about.value1Desc',
    descFallback: 'Reserve appointments 24/7 with live availability and instant confirmation.',
    chip: 'bg-blue-50 dark:bg-blue-500/10',
    accent: 'text-blue-600 dark:text-blue-400',
  },
  {
    icon: ShieldCheck,
    titleKey: 'about.value2Title',
    titleFallback: 'Trusted providers',
    descKey: 'about.value2Desc',
    descFallback: 'Every clinic, salon and spa is licensed and manually verified by our team.',
    chip: 'bg-emerald-50 dark:bg-emerald-500/10',
    accent: 'text-emerald-600 dark:text-emerald-400',
  },
  {
    icon: CreditCard,
    titleKey: 'about.value3Title',
    titleFallback: 'Secure payments',
    descKey: 'about.value3Desc',
    descFallback: 'Safe online transactions with Knet, MyFatoorah and major cards.',
    chip: 'bg-teal-50 dark:bg-teal-500/10',
    accent: 'text-teal-600 dark:text-teal-400',
  },
  {
    icon: MapPinned,
    titleKey: 'about.value4Title',
    titleFallback: 'Wide network',
    descKey: 'about.value4Desc',
    descFallback: 'Clinics, salons, spas and wellness centres across Kuwait.',
    chip: 'bg-violet-50 dark:bg-violet-500/10',
    accent: 'text-violet-600 dark:text-violet-400',
  },
];

const AboutUs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tiltRef = useTilt(4);

  return (
    <div data-testid="about-us-page" className="relative overflow-hidden bg-white dark:bg-gray-950 grain">
      <div className="absolute inset-0 dot-grid opacity-50 [mask-image:radial-gradient(60%_45%_at_50%_0%,#000,transparent)]" />
      <span className="aurora-blob w-[30rem] h-[30rem] -top-40 start-[-8rem] bg-blue-400/45 dark:bg-blue-600/30 animate-aurora" />
      <span className="aurora-blob w-[24rem] h-[24rem] top-32 end-[-6rem] bg-emerald-400/40 dark:bg-emerald-500/25 animate-aurora-slow" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <Reveal className="max-w-3xl text-start">
          <span className="inline-flex items-center gap-2 rounded-full glass-chip px-3.5 py-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-200">
              {t('about.badge', 'About EnayaHub')}
            </span>
          </span>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-gray-900 dark:text-white">
            {t('about.titleLead', 'Connecting Kuwait with')}{' '}
            <span className="text-gradient-brand">{t('about.titleAccent', 'care you can trust')}</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
            {t(
              'about.subtitle',
              'EnayaHub brings Kuwait’s clinics, salons and spas into one calm, reliable booking experience — for customers and for the businesses that serve them.'
            )}
          </p>
        </Reveal>

        <Reveal delay={120} className="tilt-stage mt-14">
          <div
            ref={tiltRef}
            className="tilt-card relative rounded-[2rem] glass-panel p-8 sm:p-12 overflow-hidden"
          >
            <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400">
              {t('about.missionLabel', 'Our mission')}
            </p>
            <p className="font-display mt-5 text-2xl sm:text-3xl font-bold leading-snug tracking-tight text-gray-900 dark:text-white max-w-3xl">
              {t(
                'about.mission',
                'To simplify access to health, beauty and wellness in Kuwait by connecting people with trusted providers through seamless online booking.'
              )}
            </p>
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {VALUES.map((value, i) => {
            const Icon = value.icon;
            return (
              <Reveal key={value.titleKey} delay={i * 100}>
                <div className="h-full rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-7 lift hover:shadow-2xl">
                  <span className={`grid place-items-center w-12 h-12 rounded-2xl ${value.chip}`}>
                    <Icon className={`w-5 h-5 ${value.accent}`} />
                  </span>
                  <h3 className="font-display mt-5 text-lg font-bold text-gray-900 dark:text-white">
                    {t(value.titleKey, value.titleFallback)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    {t(value.descKey, value.descFallback)}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={100} className="mt-12">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-600 to-emerald-500 px-6 py-12 sm:px-12 sm:py-14 grain">
            <span className="aurora-blob w-[20rem] h-[20rem] -top-24 end-[-4rem] bg-white/25 animate-aurora" />
            <div className="relative max-w-2xl text-start">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                {t('about.joinTitle', 'Grow your business with EnayaHub')}
              </h2>
              <p className="mt-4 text-base text-white/80">
                {t(
                  'about.joinDesc',
                  'List your clinic, salon or spa, manage your calendar, and reach thousands of customers looking for exactly what you offer.'
                )}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/pricing')}
                  data-testid="about-provider-join-cta"
                  className="btn-shine group inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-blue-700 shadow-xl lift"
                >
                  {t('siteNav.forProviders', 'For Providers')}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
                </button>
                <button
                  onClick={() => navigate('/healthcare-providers')}
                  data-testid="about-browse-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/10 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-sm lift hover:bg-white/20"
                >
                  {t('landing.exploreAll', 'Explore all providers')}
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={80} className="mt-12 text-start">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('about.contactPrompt', 'Questions, or want to list your facility?')}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="mailto:support@enayahub.net"
              data-testid="about-email-link"
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-5 py-3 text-sm font-semibold text-gray-900 dark:text-white lift hover:border-blue-300 dark:hover:border-blue-400/40"
            >
              <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              {t('about.emailUs', 'Email us')}
            </a>
            <a
              href="tel:+96565527758"
              data-testid="about-phone-link"
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 px-5 py-3 text-sm font-semibold text-gray-900 dark:text-white lift hover:border-emerald-300 dark:hover:border-emerald-400/40"
            >
              <Phone className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className="numeric-ltr">+965 6552 7758</span>
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default AboutUs;
